'use client';
import React, { useState, Fragment } from 'react';

function PhenotypeCountList({
  diagnosed_count,
  phenotypes,
  itemHeight = 70,
}: {
  diagnosed_count: number;
  phenotypes: Record<string, number>;
  itemHeight: number;
}) {
  // 초기 상태: 모두 false로 설정된 배열
  const [hoverState, setHoverState] = useState(
    Array(Object.keys(phenotypes).length).fill(false),
  );
  const handleMouseEnter = (
    e: React.MouseEvent<HTMLDivElement>,
    idx: number,
  ) => {
    e.currentTarget.classList.add('hovered');
    const next = [...hoverState];
    next[idx] = true;
    setHoverState(next);
  };
  const handleMouseLeave = (
    e: React.MouseEvent<HTMLDivElement>,
    idx: number,
  ) => {
    e.currentTarget.classList.remove('hovered');
    const next = [...hoverState];
    next[idx] = false;
    setHoverState(next);
  };

  const isHovered = hoverState.some(item => item === true);

  return (
    <div
      className={
        isHovered
          ? '[&_dd_.graph:hover~.info]:opacity-100 [&_dd_.graph:not(:hover)]:bg-3b-blue-light-600/50 [&_dd_.info]:opacity-50'
          : ''
      }
    >
      {/* Mobile: flex column layout, Desktop: grid with label column */}
      <dl
        className="flex flex-col gap-5 text-[14px] md:absolute md:left-0 md:right-0 md:top-0 md:grid md:grid-cols-[244px_1fr] md:gap-0"
        style={{
          // @ts-expect-error CSS variables are not typed
          '--item-height': `${itemHeight}px`,
        }}
      >
        {Object.keys(phenotypes).map((key, idx) => {
          const percent = ((phenotypes[key] / diagnosed_count) * 100).toFixed(
            1,
          );
          const count = phenotypes[key];
          const percentRounded = Math.max(1, Math.round(Number(percent)));
          const showInfoOutside = percentRounded < 15;
          const textVisibility = percentRounded < 15 ? 'hidden' : 'revert';
          const graphCol = `1/${percentRounded + 1}`;

          return (
            <Fragment key={idx}>
              {/* Mobile: column wrapper for each item */}
              <div className="flex flex-col md:contents">
                <dt className="m-0 pb-1 text-[14px] font-semibold leading-[22px] tracking-[-0.128px] text-3b-text-primary md:flex md:h-[var(--item-height)] md:items-center md:pb-0 md:pr-[10px] md:text-[16px] md:leading-[26px] md:tracking-[-0.176px]">
                  {key}
                </dt>
                <dd className="m-0 md:flex md:h-[var(--item-height)] md:items-center">
                  <div className="grid w-full grid-cols-[repeat(100,1fr)] [&_span]:text-xs [&_span]:leading-[18px] [&_span]:tracking-[0.016px] md:[&_span]:text-sm">
                    <div
                      aria-label={`${key}: ${count} patients (${percent}%)`}
                      className="graph group relative flex cursor-pointer items-center justify-end rounded-r-lg bg-3b-blue-light-600 px-[10px] py-[6px] hover:bg-3b-blue-light-600 focus:outline-none focus:ring-2 focus:ring-3b-blue-light-400 focus:ring-offset-2 md:py-[11px]"
                      style={{
                        gridColumn: graphCol,
                      }}
                      onMouseEnter={e => handleMouseEnter(e, idx)}
                      onMouseLeave={e => handleMouseLeave(e, idx)}
                    >
                      &nbsp;
                      <span
                        className="whitespace-nowrap text-right text-3b-background-surface"
                        style={{
                          visibility: textVisibility,
                        }}
                      >
                        <span className="font-semibold">{count}</span>
                        <span className="font-normal"> ({percent}%)</span>
                      </span>
                      <div className="tooltip absolute left-1/2 top-full z-[2] mt-2 hidden w-[226px] -translate-x-1/2 flex-col items-start gap-1 rounded-md bg-3b-background-contrast p-3 md:group-hover:flex">
                        <strong className="text-xs text-3b-text-contrast">
                          Patient Count: {count} ({percent}%)
                        </strong>
                        <p className="text-xs leading-[18px] text-3b-text-contrast">
                          % of total patients presenting each phenotype is shown
                          in parentheses.
                        </p>
                      </div>
                    </div>
                    <div
                      className={`info items-center text-3b-blue-light-600 ${showInfoOutside ? 'flex' : 'hidden'}`}
                      style={{
                        gridColumn: `${percentRounded + 1}/-1`,
                      }}
                    >
                      <span className="whitespace-nowrap pl-[10px]">
                        <span className="font-bold">{count}</span>
                        <span className="font-normal"> ({percent}%)</span>
                      </span>
                    </div>
                  </div>
                </dd>
              </div>
            </Fragment>
          );
        })}
      </dl>
    </div>
  );
}

export default PhenotypeCountList;
